import { Injectable, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LoadingService } from './loading.service';
import { ToastService } from './toast.service';
import { environment as env } from '../../environments/environment';

// Per-user preferences client. Central cache lives in the `preferences`
// signal so the sidebar, manage-pins page, and the pinned-iframe route
// all render from the same source without re-fetching. Load-on-boot
// pattern: consume as `pinnedScanners()` computed anywhere in the SPA.
//
// Cache invalidation: any successful save writes back to the signal so
// the sidebar stays in sync with edits made on the manage page — no
// second GET needed.

export interface PinnedScanner {
  key: string;
  label: string;
  url: string;
}

export interface UserPreferences {
  _id: string;
  pinned_scanners: PinnedScanner[];
  updated_at?: string;
  updated_by_email?: string;
}

@Injectable({ providedIn: 'root' })
export class UserPreferencesService {
  private _prefs = signal<UserPreferences | null>(null);
  private _loaded = signal(false);
  private _loading = signal(false);

  // Sidebar Pinned-section collapsed state. Persisted per browser via
  // localStorage — not synced to the server since it's a UI preference
  // that legitimately varies by device (a big monitor might keep it
  // expanded; a laptop might collapse it).
  private readonly _COLLAPSE_KEY = 'f2admin.pinned-section-collapsed';
  // Initialize to false; hydrate from localStorage in the constructor so
  // we're not depending on class-field-init-order gymnastics with a
  // method-call-in-field-initializer that TypeScript strict may reject.
  private _sidebarCollapsed = signal(false);

  // Computed views the SPA consumes.
  readonly preferences = computed(() => this._prefs());
  readonly pinnedScanners = computed(() => this._prefs()?.pinned_scanners || []);
  readonly loaded = computed(() => this._loaded());
  readonly sidebarCollapsed = computed(() => this._sidebarCollapsed());

  toggleSidebarCollapsed() {
    const next = !this._sidebarCollapsed();
    this._sidebarCollapsed.set(next);
    try { localStorage.setItem(this._COLLAPSE_KEY, next ? '1' : '0'); } catch { /* private mode etc. */ }
  }

  constructor(
    private http: HttpClient,
    private loadingService: LoadingService,
    private toast: ToastService,
  ) {
    // Hydrate the sidebar-collapsed signal from localStorage. Safe to
    // touch localStorage here — constructor runs at first inject, well
    // after the DOM is available.
    try {
      if (localStorage.getItem(this._COLLAPSE_KEY) === '1') {
        this._sidebarCollapsed.set(true);
      }
    } catch { /* private mode etc. */ }
  }

  private _opts() { return { withCredentials: true }; }
  private _base() { return `${env.apiUrl}/rest/user-preferences`; }

  // In-flight load promise. When multiple callers race to loadOnce()
  // during app boot, they all await the SAME fetch — the previous
  // `_loading` short-circuit returned immediately without waiting for
  // the outstanding HTTP, so a direct-navigation to /admin/pinned/:key
  // could resolve the pin before _prefs was populated (IT-F2-67).
  private _loadPromise: Promise<void> | null = null;

  /** Load on app boot. Idempotent — skips if already loaded, shares the
   *  in-flight fetch when concurrent callers race. */
  async loadOnce(): Promise<void> {
    if (this._loaded()) return;
    if (this._loadPromise) return this._loadPromise;
    this._loadPromise = this._doLoad();
    try { await this._loadPromise; } finally { this._loadPromise = null; }
  }

  private async _doLoad(): Promise<void> {
    this._loading.set(true);
    const id = this.loadingService.add('Loading preferences');
    try {
      const res: any = await this.http.get(`${this._base()}/me`, this._opts()).toPromise();
      if (res?.preferences) {
        this._prefs.set(res.preferences as UserPreferences);
      }
      this._loaded.set(true);
    } catch (e: any) {
      // Non-fatal — a 401 means we're not yet authenticated; the auth
      // guard will bounce and reload will call loadOnce again on the
      // authed side.
      if (e?.status !== 401) {
        console.warn('UserPreferencesService.loadOnce failed:', e?.message || e);
      }
    } finally {
      this._loading.set(false);
      this.loadingService.remove(id);
    }
  }

  /** Force refresh (bypasses the loaded gate). Used after out-of-band edits. */
  async refresh(): Promise<void> {
    this._loaded.set(false);
    await this.loadOnce();
  }

  /**
   * Replace the pinned-scanners list. Writes to the signal on success
   * so all consumers refresh without a second GET.
   */
  async savePinnedScanners(pins: PinnedScanner[]): Promise<boolean> {
    const id = this.loadingService.add('Saving pinned scanners');
    const url = `${this._base()}/me/pinned-scanners`;
    try {
      const res: any = await this.http.put(url, { pins }, this._opts()).toPromise();
      if (res?.preferences) {
        this._prefs.set(res.preferences as UserPreferences);
      }
      return true;
    } catch (e: any) {
      const msg = e?.error?.errors?.[0]?.message || e?.error?.message || e?.message || 'Save failed';
      this.toast.error('Pinned scanners save failed', msg);
      return false;
    } finally {
      this.loadingService.remove(id);
    }
  }

  /** Find a pin by key. Used by the iframe route. */
  findPin(key: string): PinnedScanner | null {
    return this.pinnedScanners().find(p => p.key === key) || null;
  }

  /**
   * Mint a fresh one-shot sid on the caller's OWN identity and append
   * ?sid=<value> to the URL. Returns the SSO-primed URL or the raw URL
   * on failure (best-effort — if the mint fails the iframe should
   * still try to render + let the target scanner handle its own auth
   * bounce).
   */
  async ssoUrlForOpen(rawUrl: string): Promise<string> {
    if (!rawUrl) return rawUrl;
    try {
      const res: any = await this.http.post(
        `${this._base()}/me/mint-sid-for-url`,
        { url: rawUrl },
        this._opts(),
      ).toPromise();
      if (res?.ok && typeof res.url === 'string') return res.url;
      return rawUrl;
    } catch (e: any) {
      // Don't toast — the caller renders a fallback flow. Log so a
      // consistent failure shows up in dev tools.
      console.warn('ssoUrlForOpen: mint failed, using raw URL', e?.message || e);
      return rawUrl;
    }
  }

  /**
   * Synchronous URL wrap: returns a same-origin redirect URL that the
   * browser can hit directly (as an iframe src or anchor href) and the
   * backend responds with 302 to `<url>?sid=<value>`. Avoids the
   * async-fetch-then-set dance — good for template bindings where you
   * can't await. Server-side mint means cookies auth automatically.
   */
  ssoRedirectUrl(rawUrl: string): string {
    if (!rawUrl) return rawUrl;
    return `${this._base()}/me/sso-redirect?url=${encodeURIComponent(rawUrl)}`;
  }
}
